import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight, Asterisk as AsteriskIcon } from "lucide-react";
import { Asterisk } from "@/components/Brand";
import NavigationHeader from "@/components/NavigationHeader";
import { IMAGES, LOWDOWN_POSTS, liveLowdownPosts } from "@/data/content";
import { fadeUp, stagger } from "@/lib/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LowdownPage() {
  const navigate = useNavigate();

  const { data: posts } = useQuery({
    queryKey: ["lowdown"],
    queryFn: async () => {
      try {
        const { data } = await axios.get(`${API}/lowdown`);
        return data?.length ? liveLowdownPosts(data) : liveLowdownPosts(LOWDOWN_POSTS);
      } catch {
        return liveLowdownPosts(LOWDOWN_POSTS);
      }
    },
    initialData: liveLowdownPosts(LOWDOWN_POSTS),
    initialDataUpdatedAt: 0,
  });

  const [featured, ...rest] = posts;

  return (
    <div className="relative min-h-screen bg-ink font-body text-white">
      <NavigationHeader />
      <header className="border-b border-white/10 px-4 pb-16 pt-36 sm:px-6 lg:px-8 lg:pt-44">
        <div className="mx-auto max-w-7xl">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-4 inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.24em] text-cherry"
          >
            <Asterisk className="h-3.5 w-3.5" /> The Lowdown with Morgan Chanel
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            data-testid="lowdown-title"
            className="max-w-3xl font-display text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl"
          >
            Pop culture,{" "}
            <span className="font-garamond italic font-medium text-cherry">side-eyed.</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="mt-5 max-w-xl text-base leading-relaxed text-white/65 sm:text-lg"
          >
            Morgan's corner for culture, entertainment, relationships and
            internet chaos. Humor, side-eyes, unpopular opinions — and the
            occasional "now be fucking for real."
          </motion.p>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        <motion.div
          variants={stagger}
          initial="hidden"
          animate="show"
          viewport={{ once: true, amount: 0.1 }}
          className="grid grid-cols-1 gap-4 lg:grid-cols-12"
        >
          {featured && (
            <motion.article
              variants={fadeUp}
              data-testid="lowdown-post-card-1"
              onClick={() => navigate(`/lowdown/${featured.slug}`)}
              className="group relative cursor-pointer overflow-hidden rounded-3xl border border-white/10 transition-all duration-300 hover:-translate-y-1 hover:border-cherry/50 lg:col-span-12"
            >
              <img
                src={featured.image_url || IMAGES.heroSkyline}
                alt=""
                className="absolute inset-0 h-full w-full object-cover opacity-30 transition-all duration-500 group-hover:scale-[1.03] group-hover:opacity-70"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/70 to-ink/20" />
              <div className="relative max-w-2xl p-8 sm:p-12">
                <div className="flex items-center gap-3">
                  <span className="rounded-full border border-cherry/35 bg-cherry/10 px-2.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.16em] text-cherry">
                    {featured.tag === "Developing" && (
                      <span data-testid="developing-dot" className="mr-1.5 inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-cherry align-middle" />
                    )}
                    {featured.tag}
                  </span>
                  <span className="text-[11px] font-medium text-white/45">{featured.date}</span>
                </div>
                <h2 className="mt-4 font-display text-2xl font-bold leading-tight sm:text-3xl lg:text-4xl">
                  {featured.title}
                </h2>
                <p className="mt-4 text-sm leading-relaxed text-white/65 sm:text-base">
                  {featured.excerpt}
                </p>
                <span className="mt-6 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-cherry transition-transform duration-300 group-hover:translate-x-1">
                  Read the take <ArrowUpRight className="h-4 w-4" />
                </span>
              </div>
            </motion.article>
          )}

          {rest.length === 0 && (
            <motion.div
              variants={fadeUp}
              data-testid="lowdown-more-loading"
              className="flex items-center justify-center gap-3 rounded-3xl border border-dashed border-white/15 bg-panel/60 px-8 py-10 lg:col-span-12"
            >
              <Asterisk className="h-4 w-4 animate-spin-slow text-cherry" />
              <p className="text-sm font-semibold uppercase tracking-[0.18em] text-white/50">
                More lowdown loading — SZN 3 out now
              </p>
            </motion.div>
          )}

          {rest.map((post, i) => (
            <motion.article
              key={post.slug}
              variants={fadeUp}
              data-testid={`lowdown-post-card-${i + 2}`}
              onClick={() => navigate(`/lowdown/${post.slug}`)}
              className="group flex h-full cursor-pointer flex-col justify-between overflow-hidden rounded-3xl border border-white/10 bg-panel transition-all duration-300 hover:-translate-y-1 hover:border-cherry/50 lg:col-span-6"
            >
              <div className="relative h-48 overflow-hidden">
                <img
                  src={post.image_url || IMAGES.heroSkyline}
                  alt={post.title}
                  className="h-full w-full object-cover opacity-45 transition-all duration-500 group-hover:scale-110 group-hover:opacity-100"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-panel via-ink/40 to-ink/25 transition-opacity duration-500 group-hover:opacity-0" />
              </div>
              <div className="flex flex-1 flex-col justify-between p-7 sm:p-8">
                <div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="rounded-full border border-cherry/35 bg-cherry/10 px-2.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.16em] text-cherry">
                      {post.tag === "Developing" && (
                        <span data-testid="developing-dot" className="mr-1.5 inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-cherry align-middle" />
                      )}
                      {post.tag}
                    </span>
                    <span className="text-[11px] font-medium text-white/40">{post.date}</span>
                  </div>
                  <h2 className="mt-4 font-display text-xl font-bold leading-snug sm:text-2xl">
                    {post.title}
                  </h2>
                  <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-white/55">
                    {post.excerpt}
                  </p>
                </div>
                <span className="mt-6 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-cherry">
                  Read the take <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </span>
              </div>
            </motion.article>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.7 }}
          className="mt-16 rounded-3xl border border-white/10 bg-panel p-8 sm:p-10"
        >
          <div className="flex flex-wrap items-center justify-between gap-6">
            <p className="max-w-xl font-garamond text-xl italic leading-snug text-white/80 sm:text-2xl">
              "The Lowdown with Morgan Chanel — whatever has the timeline in a
              chokehold, we're decoding it."
            </p>
            <button
              data-testid="lowdown-back-home"
              onClick={() => navigate("/")}
              className="inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 text-xs font-bold uppercase tracking-[0.14em] text-white transition-colors hover:border-cherry hover:text-cherry"
            >
              <AsteriskIcon className="h-3.5 w-3.5" /> Back to the show
            </button>
          </div>
        </motion.div>
      </main>

      <footer className="border-t border-white/10 px-4 py-10 text-center sm:px-6 lg:px-8">
        <p className="text-[11px] uppercase tracking-[0.2em] text-white/35">
          © {new Date().getFullYear()} City Girls Decoded™. Raw. Unfiltered. Necessary.
        </p>
      </footer>
    </div>
  );
}
